import { useEffect, useMemo, useState } from "react";
import {
    getTaxInvoiceByPO,
    createTaxInvoice,
    updateTaxInvoice,
} from "../../services/api/stocks/invoiceService";
import { getPurchaseOrderById } from "../../services/api/boq/purchaseService";
import { useNavigate, useParams } from "react-router-dom";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default function Tax() {
    const { id } = useParams();
    const navigate = useNavigate();

    // =========================
    // STATES
    // =========================

    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);

    const [data, setData] = useState({
        purchase_order: {},
        invoices: [],
    });

    const [errors, setErrors] = useState({});

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [sortKey, setSortKey] = useState("date");

    const [form, setForm] = useState({
        id: null,
        invoice_no: "",
        invoice_date: new Date()
            .toISOString()
            .split("T")[0],
        invoice_amount: "",
        gst_amount: "",
        tds_amount: "",
        remarks: "",
        invoice_file: null,
        status: "uploaded",
    });

    // =========================
    // LOAD DATA
    // =========================

    const loadData = async () => {
        try {
            setLoading(true);

            const res = await getTaxInvoiceByPO(id);
            const poRes = await getPurchaseOrderById(id);

            // API RETURNS DIRECT ARRAY
            const invoicesData = Array.isArray(res.data.data)
                ? res.data.data
                : [];

            const purchaseOrder = poRes?.data?.data || poRes?.data || {};
            setData({
                purchase_order: purchaseOrder,
                invoices: invoicesData,
            });

        } catch (err) {
            console.error("Load Error", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [id]);

    // =========================
    // SAFE DATA
    // =========================

    const po = data?.purchase_order || {};

    let invoices = Array.isArray(data?.invoices)
        ? data.invoices
        : [];

    // =========================
    // KPI
    // =========================

    const totalInvoiceAmount = invoices.reduce(
        (a, b) =>
            a + Number(b.invoice_amount || 0),
        0
    );

    const totalGST = invoices.reduce(
        (a, b) =>
            a + Number(b.gst_amount || 0),
        0
    );

    const totalTDS = invoices.reduce(
        (a, b) =>
            a + Number(b.tds_amount || 0),
        0
    );

    const balanceAmount =
        Number(po?.total_amount || 0) -
        totalInvoiceAmount;

    // =========================
    // FILTERS
    // =========================

    invoices = invoices
        .filter((i) => {
            return (
                i.invoice_no
                    ?.toLowerCase()
                    .includes(search.toLowerCase()) ||
                i.remarks
                    ?.toLowerCase()
                    .includes(search.toLowerCase())
            );
        })
        .filter((i) => {
            return statusFilter === "All"
                ? true
                : i.status === statusFilter;
        });

    // =========================
    // SORTING
    // =========================

    invoices.sort((a, b) => {
        if (sortKey === "amount") {
            return (
                Number(b.invoice_amount) -
                Number(a.invoice_amount)
            );
        }

        if (sortKey === "date") {
            return (
                new Date(b.invoice_date) -
                new Date(a.invoice_date)
            );
        }

        return (a.invoice_no || "").localeCompare(
            b.invoice_no || ""
        );
    });

    // =========================
    // NET PAYABLE
    // =========================

    const netPayable = useMemo(() => {
        return (
            Number(form.invoice_amount || 0) +
            Number(form.gst_amount || 0) -
            Number(form.tds_amount || 0)
        );
    }, [
        form.invoice_amount,
        form.gst_amount,
        form.tds_amount,
    ]);

    // =========================
    // HANDLE CHANGE
    // =========================

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "invoice_file") {
            setForm((prev) => ({
                ...prev,
                invoice_file:
                    files && files.length > 0
                        ? files[0]
                        : null,
            }));
        } else {
            setForm((prev) => ({
                ...prev,
                [name]: value,
            }));
        }

        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    // =========================
    // VALIDATION
    // =========================

    const validateForm = () => {
        let newErrors = {};

        if (!form.invoice_no.trim()) {
            newErrors.invoice_no =
                "Invoice number is required";
        }

        if (!form.invoice_date) {
            newErrors.invoice_date =
                "Invoice date is required";
        }

        if (!form.invoice_amount) {
            newErrors.invoice_amount =
                "Invoice amount is required";
        }

        if (
            Number(form.invoice_amount || 0) <= 0
        ) {
            newErrors.invoice_amount =
                "Amount must be greater than 0";
        }

        if (
            form.invoice_file &&
            form.invoice_file.size >
            10 * 1024 * 1024
        ) {
            newErrors.invoice_file =
                "Max file size is 10MB";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    // =========================
    // RESET FORM
    // =========================

    const resetForm = () => {
        setForm({
            id: null,
            invoice_no: "",
            invoice_date: new Date()
                .toISOString()
                .split("T")[0],
            invoice_amount: "",
            gst_amount: "",
            tds_amount: "",
            remarks: "",
            invoice_file: null,
            status: "uploaded",
        });

        setErrors({});
    };

    // =========================
    // SUBMIT
    // =========================

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            setSubmitLoading(true);

            const payload = new FormData();

            payload.append(
                "purchase_order_id",
                id
            );

            payload.append(
                "invoice_no",
                form.invoice_no
            );

            payload.append(
                "invoice_date",
                form.invoice_date
            );

            payload.append(
                "invoice_amount",
                form.invoice_amount
            );

            payload.append(
                "gst_amount",
                form.gst_amount || 0
            );

            payload.append(
                "tds_amount",
                form.tds_amount || 0
            );

            payload.append(
                "payable_amount",
                netPayable
            );

            payload.append(
                "remarks",
                form.remarks
            );

            payload.append(
                "status",
                form.status
            );

            if (
                form.invoice_file &&
                form.invoice_file instanceof File
            ) {
                payload.append(
                    "invoice_file",
                    form.invoice_file
                );
            }

            if (form.id) {
                await updateTaxInvoice(
                    form.id,
                    payload
                );
            } else {
                await createTaxInvoice(payload);
            }

            resetForm();

            loadData();
        } catch (err) {
            console.error(err);
            alert(
                "Something went wrong while saving invoice"
            );
        } finally {
            setSubmitLoading(false);
        }
    };

    // =========================
    // EDIT
    // =========================

    const handleEdit = (inv) => {
        setForm({
            id: inv.id,
            invoice_no: inv.invoice_no || "",
            invoice_date:
                inv.invoice_date || "",
            invoice_amount:
                inv.invoice_amount || "",
            gst_amount:
                inv.gst_amount || "",
            tds_amount:
                inv.tds_amount || "",
            remarks: inv.remarks || "",
            invoice_file: null,
            status:
                inv.status || "uploaded",
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    // =========================
    // EXPORT EXCEL
    // =========================

    const exportExcel = () => {
        const rows = invoices.map((i) => ({
            Invoice: i.invoice_no,
            Date: i.invoice_date,
            Amount: i.invoice_amount,
            GST: i.gst_amount,
            TDS: i.tds_amount,
            Payable: i.payable_amount,
            Status: i.status,
        }));

        const ws =
            XLSX.utils.json_to_sheet(rows);

        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            wb,
            ws,
            "Invoices"
        );

        XLSX.writeFile(
            wb,
            `tax_invoice_${id}.xlsx`
        );
    };

    // =========================
    // EXPORT PDF
    // =========================

    const exportPDF = () => {
        const doc = new jsPDF();

        doc.text(
            `Tax Invoice Report - ${po?.po_number || ""
            }`,
            14,
            14
        );

        doc.autoTable({
            startY: 20,

            head: [
                [
                    "Invoice",
                    "Date",
                    "Amount",
                    "GST",
                    "TDS",
                    "Payable",
                    "Status",
                ],
            ],

            body: invoices.map((i) => [
                i.invoice_no,
                i.invoice_date,
                i.invoice_amount,
                i.gst_amount,
                i.tds_amount,
                i.payable_amount,
                i.status,
            ]),
        });

        doc.save(
            `tax_invoice_${id}.pdf`
        );
    };

    // =========================
    // LOADING
    // =========================

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-100">
                <div className="bg-white px-6 py-4 rounded-2xl shadow">
                    Loading Tax Invoices...
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100 p-4 md:p-6">

            {/* HEADER */}

            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">

                <div>

                    <button
                        onClick={() => navigate(-1)}
                        className="text-blue-600 text-sm mb-2 hover:text-blue-800"
                    >
                        ← Back
                    </button>

                    <h1 className="text-3xl font-bold text-slate-800">
                        Tax Invoice Management
                    </h1>

                    <p className="text-slate-500 mt-1">
                        Purchase Order :
                        <span className="font-semibold ml-2 text-slate-700">
                            {po?.po_number || "-"}
                        </span>
                    </p>

                </div>

                <div className="flex flex-wrap gap-3">

                    <button
                        onClick={exportExcel}
                        className="bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow-sm"
                    >
                        Export Excel
                    </button>

                    <button
                        onClick={exportPDF}
                        className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium shadow-sm"
                    >
                        Export PDF
                    </button>

                </div>

            </div>

            {/* KPI */}

            <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-6">

                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs uppercase text-slate-500">
                        PO Total
                    </p>

                    <h2 className="text-xl font-bold mt-2">
                        ₹{Number(po?.total_amount || 0).toLocaleString()}
                    </h2>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs uppercase text-slate-500">
                        Invoice Total
                    </p>

                    <h2 className="text-xl font-bold text-green-600 mt-2">
                        ₹{totalInvoiceAmount.toLocaleString()}
                    </h2>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs uppercase text-slate-500">
                        GST Total
                    </p>

                    <h2 className="text-xl font-bold mt-2">
                        ₹{totalGST.toLocaleString()}
                    </h2>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs uppercase text-slate-500">
                        TDS Total
                    </p>

                    <h2 className="text-xl font-bold text-orange-600 mt-2">
                        ₹{totalTDS.toLocaleString()}
                    </h2>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs uppercase text-slate-500">
                        Balance
                    </p>

                    <h2 className="text-xl font-bold text-red-600 mt-2">
                        ₹{balanceAmount.toLocaleString()}
                    </h2>
                </div>

            </div>

            {/* MAIN LAYOUT */}

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">

                {/* LEFT SIDE FORM */}

                <div className="xl:col-span-4">

                    <div className="sticky top-4">

                        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">

                            <div className="px-5 py-4 border-b bg-slate-50 flex items-center justify-between">

                                <div>
                                    <h2 className="text-lg font-semibold text-slate-800">
                                        {form.id
                                            ? "Update Invoice"
                                            : "Create Invoice"}
                                    </h2>

                                    <p className="text-xs text-slate-500 mt-1">
                                        Add or update tax invoice
                                    </p>
                                </div>

                                {form.id && (
                                    <button
                                        onClick={resetForm}
                                        className="text-sm text-red-600 hover:text-red-700"
                                    >
                                        Cancel
                                    </button>
                                )}

                            </div>

                            <form
                                onSubmit={handleSubmit}
                                className="p-5 space-y-5"
                            >

                                {/* GRID FORM */}

                                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">

                                    {/* Invoice No */}

                                    <div>

                                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                                            Invoice Number
                                        </label>

                                        <input
                                            type="text"
                                            name="invoice_no"
                                            placeholder="INV-2026-001"
                                            value={form.invoice_no}
                                            onChange={handleChange}
                                            className={`w-full border rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none
                ${errors.invoice_no
                                                    ? "border-red-500"
                                                    : "border-slate-300"
                                                }`}
                                        />

                                        {errors.invoice_no && (
                                            <p className="text-red-500 text-xs mt-1">
                                                {errors.invoice_no}
                                            </p>
                                        )}

                                    </div>

                                    {/* Date */}

                                    <div>

                                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                                            Invoice Date
                                        </label>

                                        <input
                                            type="date"
                                            name="invoice_date"
                                            value={form.invoice_date}
                                            onChange={handleChange}
                                            className={`w-full border rounded-xl p-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none
                                            ${errors.invoice_date
                                                    ? "border-red-500"
                                                    : "border-slate-300"
                                                }`}
                                        />

                                    </div>

                                    {/* Invoice Amount */}

                                    <div>

                                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                                            Invoice Amount
                                        </label>

                                        <input
                                            type="number"
                                            name="invoice_amount"
                                            value={form.invoice_amount}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            className="w-full border border-slate-300 rounded-xl p-3 text-sm"
                                        />

                                    </div>

                                    {/* GST */}

                                    <div>

                                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                                            GST Amount
                                        </label>

                                        <input
                                            type="number"
                                            name="gst_amount"
                                            value={form.gst_amount}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            className="w-full border border-slate-300 rounded-xl p-3 text-sm"
                                        />

                                    </div>

                                    {/* TDS */}

                                    <div>

                                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                                            TDS Amount
                                        </label>

                                        <input
                                            type="number"
                                            name="tds_amount"
                                            value={form.tds_amount}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            className="w-full border border-slate-300 rounded-xl p-3 text-sm"
                                        />

                                    </div>

                                    {/* STATUS */}

                                    <div>

                                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                                            Status
                                        </label>

                                        <select
                                            name="status"
                                            value={form.status}
                                            onChange={handleChange}
                                            className="w-full border border-slate-300 rounded-xl p-3 text-sm"
                                        >
                                            <option value="uploaded">
                                                Uploaded
                                            </option>

                                            <option value="verified">
                                                Verified
                                            </option>

                                            <option value="pending">
                                                Pending
                                            </option>
                                        </select>

                                    </div>

                                    {/* FILE */}

                                    <div>

                                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                                            Upload Invoice
                                        </label>

                                        <input
                                            type="file"
                                            name="invoice_file"
                                            onChange={handleChange}
                                            className="w-full border border-slate-300 rounded-xl p-3 text-sm"
                                        />

                                    </div>

                                    {/* NET PAYABLE */}

                                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 flex flex-col justify-center">

                                        <p className="text-sm text-slate-600">
                                            Net Payable
                                        </p>

                                        <h2 className="text-3xl font-bold text-green-700 mt-1">
                                            ₹{netPayable.toLocaleString()}
                                        </h2>

                                    </div>

                                </div>

                                {/* REMARKS */}

                                <div>

                                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                                        Remarks
                                    </label>

                                    <textarea
                                        rows={4}
                                        name="remarks"
                                        value={form.remarks}
                                        onChange={handleChange}
                                        placeholder="Remarks..."
                                        className="w-full border border-slate-300 rounded-xl p-3 text-sm"
                                    />

                                </div>

                                {/* BUTTONS */}

                                <div className="flex gap-3 pt-2">

                                    <button
                                        type="submit"
                                        disabled={submitLoading}
                                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-medium shadow-sm"
                                    >
                                        {submitLoading
                                            ? "Saving..."
                                            : form.id
                                                ? "Update Invoice"
                                                : "Save Invoice"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-5 bg-slate-200 hover:bg-slate-300 rounded-xl font-medium"
                                    >
                                        Reset
                                    </button>

                                </div>

                            </form>
                        </div>

                    </div>

                </div>

                {/* RIGHT SIDE TABLE */}

                <div className="xl:col-span-8">

                    {/* FILTER */}

                    <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-4 mb-5">

                        <div className="flex flex-col lg:flex-row gap-3">

                            <input
                                type="text"
                                placeholder="Search invoice..."
                                value={search}
                                onChange={(e) =>
                                    setSearch(e.target.value)
                                }
                                className="border border-slate-300 rounded-xl p-3 w-full lg:w-1/3"
                            />

                            <select
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(e.target.value)
                                }
                                className="border border-slate-300 rounded-xl p-3"
                            >
                                <option value="All">
                                    All Status
                                </option>

                                <option value="uploaded">
                                    Uploaded
                                </option>

                                <option value="verified">
                                    Verified
                                </option>

                                <option value="pending">
                                    Pending
                                </option>

                            </select>

                        </div>

                    </div>

                    {/* TABLE */}

                    <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">

                        <div className="overflow-auto max-h-[700px]">

                            <table className="w-full text-sm">

                                <thead className="bg-slate-100 sticky top-0 z-10">

                                    <tr>
                                        <th className="p-4 text-left">SlNo</th>
                                        <th className="p-4 text-left">Invoice</th>
                                        <th className="p-4 text-left">Amount</th>
                                        <th className="p-4 text-left">Payable</th>
                                        <th className="p-4 text-left">Status</th>
                                        <th className="p-4 text-left">Action</th>
                                    </tr>

                                </thead>

                                <tbody>

                                    {invoices.map((i) => (

                                        <tr
                                            key={i.id}
                                            className="border-t hover:bg-slate-50 transition"
                                        >
                                            <td className="p-4">{invoices.indexOf(i) + 1}</td>
                                            <td className="p-4">
                                                <div className="font-semibold text-slate-800">
                                                    {i.invoice_no}
                                                </div>

                                                <div className="text-xs text-slate-500 mt-1">
                                                    {i.invoice_date}
                                                </div>

                                            </td>

                                            <td className="p-4">

                                                <div>
                                                    ₹{Number(i.invoice_amount).toLocaleString()}
                                                </div>

                                                <div className="text-xs text-slate-500 mt-1">
                                                    GST : ₹{Number(i.gst_amount).toLocaleString()}
                                                </div>

                                            </td>

                                            <td className="p-4 font-bold text-green-700">
                                                ₹{Number(i.payable_amount).toLocaleString()}
                                            </td>

                                            <td className="p-4">

                                                <span
                                                    className={`px-3 py-1 rounded-full text-xs font-semibold
                                            ${i.status === "verified"
                                                            ? "bg-green-100 text-green-700"
                                                            : i.status === "pending"
                                                                ? "bg-yellow-100 text-yellow-700"
                                                                : "bg-blue-100 text-blue-700"
                                                        }`}
                                                >
                                                    {i.status}
                                                </span>

                                            </td>

                                            <td className="p-4">

                                                <button
                                                    onClick={() => handleEdit(i)}
                                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                    Edit
                                                </button>

                                            </td>

                                        </tr>

                                    ))}

                                </tbody>

                            </table>

                        </div>

                    </div>

                </div>

            </div>

        </div>
    );
}